import { CSSProperties } from "react";

type Provider = "apple" | "google";

interface OAuthButtonProps {
  provider: Provider;
  onClick: () => void;
  disabled?: boolean;
  /** Overrides the default "Sign in with …" label. */
  label?: string;
}


// Brand-mandated styling (Apple HIG + Google Sign-In branding guidelines).
// Hex values are intentionally hardcoded and must not be themed.
const BRAND = {
  apple: {
    bg: "#000000",
    bgHover: "#1a1a1a",
    fg: "#ffffff",
    border: "none",
    radius: "6px",
    minHeight: "44px",
    padding: "12px 16px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: "17px",
    fontWeight: 600,
    label: "Sign in with Apple",
  },
  google: {
    bg: "#ffffff",
    bgHover: "#ffffff",
    fg: "#3c4043",
    border: "1px solid #dadce0",
    radius: "4px",
    minHeight: "40px",
    padding: "10px 16px",
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: "14px",
    fontWeight: 500,
    label: "Sign in with Google",
  },
} as const;

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

export const OAuthButton = ({ provider, onClick, disabled, label }: OAuthButtonProps) => {
  const b = BRAND[provider];
  const baseShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
  const hoverShadow = "0 4px 8px 0 rgba(0, 0, 0, 0.1)";

  const style: CSSProperties = {
    width: "100%",
    minHeight: b.minHeight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: b.padding,
    backgroundColor: b.bg,
    color: b.fg,
    border: b.border,
    borderRadius: b.radius,
    boxShadow: baseShadow,
    fontFamily: b.fontFamily,
    fontSize: b.fontSize,
    fontWeight: b.fontWeight,
    lineHeight: 1,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "all 0.15s ease-in-out",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={style}
      onMouseEnter={(e) => {
        if (disabled) return;
        if (provider === "apple") e.currentTarget.style.backgroundColor = BRAND.apple.bgHover;
        else e.currentTarget.style.boxShadow = hoverShadow;
      }}
      onMouseLeave={(e) => {
        if (provider === "apple") e.currentTarget.style.backgroundColor = BRAND.apple.bg;
        else e.currentTarget.style.boxShadow = baseShadow;
      }}
    >
      {provider === "apple" ? <AppleIcon /> : <GoogleIcon />}
      <span style={provider === "google" ? { color: BRAND.google.fg } : undefined}>{label ?? b.label}</span>
    </button>
  );
};

export default OAuthButton;
